/**
 * i18n.ts
 *
 * Initializes i18next with per-route, lazy-loaded locale chunks.
 *
 * Each file under ./locales/<lng>/<chunk>.json is split by Vite into its own
 * dynamic chunk. Only the bootstrap chunks (`common` + `auth`) are loaded
 * synchronously here — enough to render the unauthenticated entry (login /
 * register / verify) without flashes of raw i18n keys. Everything else is
 * loaded on demand via `ensureI18nChunks(...names)`, which route wrappers call
 * right before mounting their page.
 *
 * Chunks are merged into the single flat `translation` namespace via
 * `addResourceBundle(lng, 'translation', data, true, true)`, so every existing
 * `t('fleet.orderDialog.cancelOrder')` call keeps working unchanged.
 *
 * Top-level `await` blocks main.tsx's import of this module until the
 * bootstrap chunks are in place.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Vite glob that enumerates every chunked locale JSON. Structure:
 *   './locales/en/common.json'  -> () => import(...)
 *   './locales/de/reports.json' -> () => import(...)
 */
const chunkLoaders = import.meta.glob<{ default: Record<string, unknown> }>(
  './locales/*/*.json',
);

/** Chunks loaded synchronously at bootstrap — every page needs these. */
const BOOTSTRAP_CHUNKS = ['common', 'auth'] as const;

/**
 * Tracks chunks already fetched for each language. Used to avoid re-fetching
 * on repeat calls and to know which chunks to re-fetch on language change.
 * Keyed as `${lng}:${chunk}`.
 */
const loaded = new Set<string>();

/**
 * Tracks in-flight fetches so concurrent callers for the same chunk share a
 * single network round-trip (e.g. two lazyPage boundaries asking for the same
 * chunk during a Suspense fallback).
 */
const inFlight = new Map<string, Promise<void>>();

/**
 * Resolve a single chunk for a given language, register it with i18next, and
 * mark it loaded. No-ops if the chunk is missing for that language (e.g. a
 * new page added to only one locale while translations are pending).
 */
function loadChunk(lng: string, chunk: string): Promise<void> {
  const key = `${lng}:${chunk}`;
  if (loaded.has(key)) return Promise.resolve();
  const existing = inFlight.get(key);
  if (existing) return existing;

  const path = `./locales/${lng}/${chunk}.json`;
  const loader = chunkLoaders[path];
  if (!loader) {
    loaded.add(key); // remember the miss so we don't re-probe every call
    return Promise.resolve();
  }

  const p = loader().then((mod) => {
    i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
    loaded.add(key);
  }).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, p);
  return p;
}

/**
 * Public: ensure a set of chunks is loaded in the active language (and the
 * fallback language, if different). Called by route wrappers via lazyPage()
 * and by any ad-hoc caller that needs a chunk outside the route boundary.
 */
export async function ensureI18nChunks(...chunks: string[]): Promise<void> {
  const active = i18n.language;
  const fallback = 'de';
  const jobs: Promise<void>[] = [];
  for (const c of chunks) {
    jobs.push(loadChunk(active, c));
    if (active !== fallback) jobs.push(loadChunk(fallback, c));
  }
  await Promise.all(jobs);
}

const savedLang = localStorage.getItem('kabbi_lang') ?? 'de';

await i18n.use(initReactI18next).init({
  resources:     {},          // populated lazily via loadChunk below
  lng:           savedLang,
  fallbackLng:   'de',
  interpolation: { escapeValue: false },
});

// Load bootstrap chunks for the active language (and fallback, if different)
// before main.tsx mounts React. Everything fans out in parallel.
await ensureI18nChunks(...BOOTSTRAP_CHUNKS);

// Keep <html lang="..."> in sync so native browser elements (date pickers etc.)
// use the app's locale rather than the OS locale.
document.documentElement.lang = savedLang;

/**
 * On language change, we need the *new* language to own every chunk that's
 * already registered for the old language — otherwise a user who has visited
 * /reports then flips language would see English strings everywhere on that
 * page because the German reports.json was never fetched.
 *
 * We re-fetch every known chunk in the new language, in parallel. `loaded`
 * keys like `en:reports` are independent from `de:reports`, so the set
 * naturally grows per-language.
 */
i18n.on('languageChanged', async (lng) => {
  document.documentElement.lang = lng;
  const names = new Set<string>();
  for (const key of loaded) {
    const [, chunk] = key.split(':');
    if (chunk) names.add(chunk);
  }
  // Always re-include bootstrap chunks in case this is the first time we see
  // the new language.
  for (const b of BOOTSTRAP_CHUNKS) names.add(b);
  await Promise.all([...names].map((c) => loadChunk(lng, c)));
});

export default i18n;
